//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useState } from 'react';
import { IngestFile } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import Notes from '../_common/Notes/Notes';
import { useConfigValue } from '../_hooks/useConfigValue';
import OverflowMenu from '../_common/OverflowMenu/OverflowMenu';
import PolarisIcon from '../_common/PolarisIcon';
import OverflowMenuItem from '../_common/OverflowMenu/OverflowMenuItem';
import { useHistory } from 'react-router-dom';
import { Button, TextField, Typography, Grid } from '@mui/material';
import { LabelValue, Modal } from '../_common';
import { TableStatus } from '../_common/Table';
import { useToast } from '../_common/Toast';
import axios from '@/_utils/axios';
import { useAppDispatch } from '@/redux/hooks';
import { applyFilter, clearFilters, setCurrentTable, getData } from '@/redux/slices/dataTableSlice';
import EditIngestFileDialog from './EditIngestFileDialog';
import History from '../_common/History/History';

type IngestManagementOverflowMenuProps = { data: IngestFile; vertical?: boolean };
const IngestManagementOverflowMenu = ({ data, vertical = false }: IngestManagementOverflowMenuProps) => {
  const version = useConfigValue(['ingestManagementConfig', 'version']) || 'v1';
  const allowAutoIngestRegister = useConfigValue(['ingestManagementConfig', 'allowAutoIngestRegister']) || false;
  const allowFailedIngestUpdate = (useConfigValue(['ingestManagementConfig', 'failedIngestUpdate']) && data.auto_ingest) || false;
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [resolveOpen, setResolveOpen] = useState(false);
  const [isEditFileIngestDialogOpen, setIsEditFileIngestDialogOpen] = useState(false);
  const [resolution, setResolution] = useState('');
  const [hasError, setHasError] = useState(false);
  const { error, success } = useToast();

  const handleResolveModalOpen = (e: React.MouseEvent<HTMLButtonElement | HTMLLIElement>) => {
    e.stopPropagation();
    setResolveOpen(true);
  };

  const handleResolveModalClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setResolution('');
    setResolveOpen(false);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (hasError) {
      setHasError(false);
    }
    setResolution(e.target.value);
  };

  const submitResolution = () => {
    if (!resolution.length) {
      setHasError(true);
      error('Please enter a resolution');
    } else {
      axios
        .post(`/ingest_manager/ingest_records/${data.id}/resolve/`, { status_reason: resolution })
        .then((res) => {
          if (!res.data.success) {
            throw new Error();
          } else {
            success('Resolution submitted');
            setResolveOpen(false);
            dispatch(getData());
          }
        })
        .catch(() => error('There was a problem resolving the Ingest.'))
        .then(() => setResolution(''));
    }
  };

  const handleClick = () => {
    history.push(`/ingest-management/${data.id}`);
    // scroll to top of page
    window.scrollTo(0, 0);
  };

  const handlePendingAssetClick = () => {
    dispatch(setCurrentTable('pendingAssets'));
    dispatch(clearFilters(undefined));
    dispatch(applyFilter({ type: 'filter_id', selections: [`A${data.pending_asset_id}`, 'job_asset_hid_filter'] }));
    history.push(`/pending-assets/`);
  };

  const viewDetailsMenuItem = (
    <OverflowMenuItem onClick={handleClick}>
      <PolarisIcon icon={'search'} style={{ height: 18 }} />
      View Details
    </OverflowMenuItem>
  );

  const viewPendingAsset = !!data.pending_asset_id && (
    <OverflowMenuItem onClick={handlePendingAssetClick}>
      <PolarisIcon icon={'link'} style={{ height: 18 }} />
      Pending Asset
    </OverflowMenuItem>
  );

  const renderViewOption = () => {
    const stage = allowFailedIngestUpdate && data.status.toLowerCase() === 'failed' ? 'failed' : data.stage;
    switch (stage) {
      case 'created':
      case 'awaiting_file':
      case 'staging':
        return null;
      case 'ready_for_ingest':
        return (
          <>
            <OverflowMenuItem onClick={handleClick}>
              <PolarisIcon icon={'link'} style={{ height: 18 }} />
              Register File
            </OverflowMenuItem>
            {viewPendingAsset}
          </>
        );
      case 'failed':
        return (
          <>
            {viewDetailsMenuItem}
            {viewPendingAsset}
            {allowFailedIngestUpdate && (
              <OverflowMenuItem data-testid="modal-open" onClick={() => setIsEditFileIngestDialogOpen(true)}>
                <PolarisIcon icon="edit" style={{ height: 18 }} /> Rename File and Retry
              </OverflowMenuItem>
            )}
          </>
        );
      default:
        return (
          <>
            {viewDetailsMenuItem}
            {viewPendingAsset}
          </>
        );
    }
  };

  const optionsToRender = renderViewOption();
  return version === 'v1' ? (
    <OverflowMenu>
      <History data={data} />
    </OverflowMenu>
  ) : (
    <Wrapper vertical={+vertical}>
      <StyledNotes data={data} badgeColor="primary" />
      <StyledHistory data={data} iconOnly />
      <StyledOverflowMenu disabled={optionsToRender === null} style={{ opacity: optionsToRender == null ? 0.35 : 1 }}>
        {optionsToRender}
      </StyledOverflowMenu>

      {data.status.toLowerCase() === 'failed' && !!allowAutoIngestRegister && (
        <>
          <StyledOverflowMenu>
            <OverflowMenuItem data-testid="modal-open" onClick={handleResolveModalOpen}>
              <PolarisIcon icon="check_double" style={{ height: 18 }} /> Mark as Resolved
            </OverflowMenuItem>
          </StyledOverflowMenu>
          <StyledModal
            loading={false}
            open={resolveOpen}
            onClose={(e) => handleResolveModalClose(e)}
            title="Mark as Resolved"
            icon={<PolarisIcon icon="check_double" height={18} />}
          >
            <Grid data-testid="mark-resolved-modal-content" container columnSpacing={6} rowSpacing={2} sx={{ mb: 4 }}>
              <LabelValue label="Ingest ID" color="primary">
                {data.hid}
              </LabelValue>
              <LabelValue label="Status" color="primary">
                <TableStatus status={data.status} />
              </LabelValue>
              <LabelValue size={{ xs: 12 }} label="File Name" color="primary">
                <Typography>
                  <strong>{data.filename || '-'}</strong>
                </Typography>
              </LabelValue>
            </Grid>

            <TextField
              fullWidth
              id="resolution"
              label="Resolution Notes"
              multiline
              minRows={4}
              maxRows={8}
              value={resolution}
              error={hasError}
              onChange={handleInput}
              inputProps={{
                'data-testid': 'resolution-notes-input'
              }}
            />

            <Grid container justifyContent="flex-end" alignItems="center" sx={{ mt: 4 }}>
              <Button data-testid="cancel-resolve-btn" variant="text" color="default" onClick={handleResolveModalClose} sx={{ mr: 2 }}>
                Cancel
              </Button>
              <Button data-testid="confirm-resolve-btn" color="primary" variant="contained" onClick={submitResolution}>
                Resolve
              </Button>
            </Grid>
          </StyledModal>
        </>
      )}
      {data.status.toLowerCase() === 'failed' && allowFailedIngestUpdate && (
        <EditIngestFileDialog isOpen={isEditFileIngestDialogOpen} ingestFile={data} onCancel={() => setIsEditFileIngestDialogOpen(false)} />
      )}
    </Wrapper>
  );
};

export default IngestManagementOverflowMenu;

const Wrapper = styled.div<{ vertical: number }>`
  display: flex;
  flex-direction: ${({ vertical }) => (vertical ? 'column' : 'row')};
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  background: ${({ theme, vertical }) =>
    vertical ? 'transparent' : theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.primary.main + '2a'};
  padding-left: ${({ theme }) => theme.spacing(0.7)};
  height: 100%;

  svg * {
    color: ${({ theme }) => theme.palette.text.primary};
    opacity: 0.6;
  }

  ${({ vertical, theme }) =>
    vertical &&
    `
  
  .MuiButtonBase-root {
    margin-bottom: ${theme.spacing(0.25)};

    
    svg {
      height: 27px;
      width: 27px;
    }
  }
  
  `}
`;

const StyledNotes = styled(Notes)`
  .MuiButtonBase-root.MuiIconButton-root {
    padding: 16px;
  }
`;

const StyledHistory = styled(History)`
  .MuiButtonBase-root.MuiIconButton-root {
    padding: 7px;
    margin: 0px;
  }
`;

const StyledOverflowMenu = styled(OverflowMenu)`
  & .MuiIconButton-root {
    opacity: 1;
  }
`;

const StyledModal = styled(Modal)`
  .MuiPaper-root {
    max-width: 450px;
    & label {
      margin-bottom: ${(props) => props.theme.spacing(1)};
    }
  }
`;
